/**
 * Top-down order of the phone list and the desktop table view: owner levels by shortest distance
 * to the target, highest level first, then the target, then its direct subsidiaries, the way the
 * graph draws them. Levels use the shortest distance, so a holder with a direct and an indirect
 * stake is in Level 1 although the graph (longest path) draws it higher.
 *
 * @module shareholderLevels
 */
import { GRAPH_COPY, NODE_KIND } from './graphConstants';

export const LEVEL_SECTION = Object.freeze({
  OWNERS: 'owners',
  TARGET: 'target',
  SUBSIDIARIES: 'subsidiaries'
});

export const levelHeading = (distance) =>
  distance === 1 ? 'Level 1 · direct shareholders' : `Level ${distance}`;

const numericShare = (node) => {
  const value = Number.parseFloat(String(node.card?.share ?? '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(value) ? value : -1;
};

// Largest share first, the aggregate last; ties keep the prepared (structural) order.
const compareRows = (a, b) => {
  const aggregateOrder =
    Number(a.kind === NODE_KIND.AGGREGATE) - Number(b.kind === NODE_KIND.AGGREGATE);
  return aggregateOrder || numericShare(b) - numericShare(a);
};

/** Owners and the aggregate, grouped by shortest distance to the target, highest level first. */
export const groupOwnersByDistance = (graph) => {
  const groups = new Map();
  (graph?.nodes || [])
    .filter((node) => node.kind === NODE_KIND.OWNER || node.kind === NODE_KIND.AGGREGATE)
    .forEach((node) => {
      const distance = Number.isFinite(node.distance) && node.distance > 0 ? node.distance : 1;
      if (!groups.has(distance)) groups.set(distance, []);
      groups.get(distance).push(node);
    });
  return [...groups.entries()]
    .sort(([a], [b]) => b - a)
    .map(([distance, nodes]) => ({ distance, nodes: [...nodes].sort(compareRows) }));
};

/** The target's direct subsidiaries, largest held share first. */
export const subsidiaryNodes = (graph) =>
  (graph?.nodes || []).filter((node) => node.kind === NODE_KIND.SUBSIDIARY).sort(compareRows);

const ownerSections = (graph) =>
  groupOwnersByDistance(graph).map(({ distance, nodes }) => ({
    key: `level-${distance}`,
    type: LEVEL_SECTION.OWNERS,
    distance,
    heading: levelHeading(distance),
    nodes
  }));

const targetSections = (graph) => {
  const target = graph.nodes.find((node) => node.kind === NODE_KIND.TARGET);
  return target
    ? [{ key: 'target', type: LEVEL_SECTION.TARGET, distance: 0, heading: null, nodes: [target] }]
    : [];
};

const subsidiarySections = (graph) => {
  const nodes = subsidiaryNodes(graph);
  return nodes.length
    ? [
        {
          key: 'subsidiaries',
          type: LEVEL_SECTION.SUBSIDIARIES,
          distance: null,
          heading: GRAPH_COPY.SUBSIDIARIES,
          nodes
        }
      ]
    : [];
};

/**
 * The list sections in top-down order: the owner levels (highest first), the target, then the
 * subsidiaries. `owners: false` or `subsidiaries: false` leaves that part out.
 *
 * @param {Object|null} graph - PreparedGraph
 * @param {{ owners?: boolean, subsidiaries?: boolean }} [options]
 * @returns {Array<{ key: string, type: string, distance: number|null, heading: string|null,
 *   nodes: Object[] }>} empty without a graph or nodes
 */
export const buildLevelSections = (graph, { owners = true, subsidiaries = true } = {}) => {
  if (!graph?.nodes?.length) return [];
  return [
    ...(owners ? ownerSections(graph) : []),
    ...targetSections(graph),
    ...(subsidiaries ? subsidiarySections(graph) : [])
  ];
};
